'use client';
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { authApi } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    role?: string;
    isActive?: boolean;
    emailNotifTransactions?: boolean;
    emailNotifMessages?: boolean;
    emailNotifAlerts?: boolean;
    emailNotifNewsletter?: boolean;
    ownerTypes?: string[] | null;
}

interface AuthState {
    user: AuthUser | null;
    accessToken: string | null;
    loading: boolean;
}

interface AuthContextValue extends AuthState {
    login: (email: string, password: string) => Promise<void>;
    register: (data: RegisterPayload) => Promise<void>;
    logout: () => Promise<void>;
    refreshProfile: () => Promise<void>;
    updateProfile: (data: {
        firstName?: string;
        lastName?: string;
        phone?: string;
        emailNotifTransactions?: boolean;
        emailNotifMessages?: boolean;
        emailNotifAlerts?: boolean;
        emailNotifNewsletter?: boolean;
    }) => Promise<void>;
    changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

export interface RegisterPayload {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    role?: string;
    ownerTypes?: string[];
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

const KEYS = {
    access: 'access_token',
    refresh: 'refresh_token',
    user: 'auth_user',
} as const;

function saveSession(accessToken: string, refreshToken: string, user: AuthUser) {
    try {
        localStorage.setItem(KEYS.access, accessToken);
        localStorage.setItem(KEYS.refresh, refreshToken);
        localStorage.setItem(KEYS.user, JSON.stringify(user));
    } catch { /* private browsing */ }
}

function clearSession() {
    try {
        localStorage.removeItem(KEYS.access);
        localStorage.removeItem(KEYS.refresh);
        localStorage.removeItem(KEYS.user);
    } catch { /* ignore */ }
}

function loadSession(): { accessToken: string | null; refreshToken: string | null; user: AuthUser | null } {
    try {
        return {
            accessToken: localStorage.getItem(KEYS.access),
            refreshToken: localStorage.getItem(KEYS.refresh),
            user: JSON.parse(localStorage.getItem(KEYS.user) ?? 'null'),
        };
    } catch {
        return { accessToken: null, refreshToken: null, user: null };
    }
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const router = useRouter();
    const [state, setState] = useState<AuthState>({ user: null, accessToken: null, loading: true });

    // Hydrate from localStorage on mount; try silent token refresh if token expired
    useEffect(() => {
        const { accessToken, refreshToken, user } = loadSession();
        if (accessToken && user) {
            setState({ user, accessToken, loading: false });
            // Silently refresh profile to pick up any server-side changes
            authApi.getProfile(accessToken)
                .then((profile: any) => {
                    const fresh = profile?.data ?? profile;
                    setState((s) => ({ ...s, user: fresh }));
                    localStorage.setItem(KEYS.user, JSON.stringify(fresh));
                })
                .catch(async () => {
                    // Token may be expired — try refresh
                    if (refreshToken) {
                        try {
                            const res = await authApi.refresh(refreshToken);
                            saveSession(res.accessToken, res.refreshToken, user);
                            setState((s) => ({ ...s, accessToken: res.accessToken }));
                        } catch {
                            clearSession();
                            setState({ user: null, accessToken: null, loading: false });
                        }
                    } else {
                        clearSession();
                        setState({ user: null, accessToken: null, loading: false });
                    }
                });
        } else {
            setState({ user: null, accessToken: null, loading: false });
        }
    }, []);

    const login = useCallback(async (email: string, password: string) => {
        const res = await authApi.login(email, password);
        const user: AuthUser = res.user?.data ?? res.user;
        saveSession(res.accessToken, res.refreshToken, user);
        setState({ user, accessToken: res.accessToken, loading: false });
    }, []);

    const register = useCallback(async (data: RegisterPayload) => {
        const res = await authApi.register(data);
        const user: AuthUser = res.user?.data ?? res.user;
        saveSession(res.accessToken, res.refreshToken, user);
        setState({ user, accessToken: res.accessToken, loading: false });
    }, []);

    const logout = useCallback(async () => {
        if (state.accessToken) {
            authApi.logout(state.accessToken).catch(() => { });
        }
        clearSession();
        setState({ user: null, accessToken: null, loading: false });
        router.push('/');
    }, [state.accessToken, router]);

    const refreshProfile = useCallback(async () => {
        if (!state.accessToken) return;
        const profile = await authApi.getProfile(state.accessToken);
        const user = (profile as any)?.data ?? profile;
        setState((s) => ({ ...s, user }));
        localStorage.setItem(KEYS.user, JSON.stringify(user));
    }, [state.accessToken]);

    const updateProfile = useCallback(async (data: {
        firstName?: string;
        lastName?: string;
        phone?: string;
        emailNotifTransactions?: boolean;
        emailNotifMessages?: boolean;
        emailNotifAlerts?: boolean;
        emailNotifNewsletter?: boolean;
    }) => {
        if (!state.accessToken) throw new Error('Non authentifié');
        const updated = await authApi.updateProfile(state.accessToken, data);
        const user = (updated as any)?.data ?? updated;
        setState((s) => ({ ...s, user }));
        localStorage.setItem(KEYS.user, JSON.stringify(user));
    }, [state.accessToken]);

    const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
        if (!state.accessToken) throw new Error('Non authentifié');
        await authApi.changePassword(state.accessToken, { currentPassword, newPassword });
    }, [state.accessToken]);

    return (
        <AuthContext.Provider value={{
            ...state,
            login,
            register,
            logout,
            refreshProfile,
            updateProfile,
            changePassword,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}

export function useRequireAuth(redirectTo = '/auth/login') {
    const { user, loading } = useAuth();
    const router = useRouter();
    useEffect(() => {
        if (!loading && !user) router.push(redirectTo);
    }, [user, loading, redirectTo, router]);
    return { user, loading };
}
